import { useContext, useEffect, useMemo, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'

import { Dialog, DialogActions, DialogContent, DialogTitle, Button, LinearProgress, Alert } from '@mui/material'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'

import { AppContext } from '/providers/app-context'
import { getCreditMatrix, requestSyncCheck, syncPasses } from '/providers/studio-api'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import {
  getCurrentSyncCounts,
  getPlatformProgress,
  hasCurrentSyncAnswer,
  hasPendingTemplateChanges,
  isPlatformCheckRunning,
  platformNeedsCheck,
} from './sync-state-utils'

const SynchroniseDialog = ({ open, setOpen, template, onCloseComplete }) => {
  const {
    apiSource,
    apiKey,
    client,
    creditBalance,
    processing,
    templates,
    setTemplates,
  } = useContext(AppContext)

  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()
  const [checking, setChecking] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [creditMatrix, setCreditMatrix] = useState([])
  const [optimisticSyncState, setOptimisticSyncState] = useState(null)
  const [optimisticBaseline, setOptimisticBaseline] = useState(null)
  const [syncStartCounts, setSyncStartCounts] = useState(null)
  const [lastQueuedSyncKey, setLastQueuedSyncKey] = useState(null)
  const [syncCompletedPopupOpen, setSyncCompletedPopupOpen] = useState(false)
  const [notifyOnBackgroundSyncComplete, setNotifyOnBackgroundSyncComplete] = useState(false)

  const currentTemplate = useMemo(() => {
    if (!template) return null
    return templates?.find(item => item.template_id === template.template_id) ?? template
  }, [template, templates])

  const displayTemplate = useMemo(() => {
    if (!currentTemplate) return null
    return optimisticSyncState
      ? { ...currentTemplate, ...optimisticSyncState }
      : currentTemplate
  }, [currentTemplate, optimisticSyncState])

  useEffect(() => {
    if (!open) {
      setChecking(false)
      setSyncing(false)
      setOptimisticSyncState(null)
      setOptimisticBaseline(null)
      setSyncStartCounts(null)
      setLastQueuedSyncKey(null)
    }
  }, [open])

  useEffect(() => {
    if (!optimisticSyncState || !optimisticBaseline || !currentTemplate) return

    const currentSnapshot = JSON.stringify(extractSyncSnapshot(currentTemplate))
    const baselineSnapshot = JSON.stringify(optimisticBaseline)

    if (currentSnapshot !== baselineSnapshot) {
      setOptimisticSyncState(null)
      setOptimisticBaseline(null)
      setChecking(false)
    }
  }, [currentTemplate, optimisticBaseline, optimisticSyncState])

  const syncCounts = getCurrentSyncCounts(displayTemplate)
  const appleProgress = getPlatformProgress(displayTemplate, 'apple')
  const googleProgress = getPlatformProgress(displayTemplate, 'google')
  const currentAnswerReady = hasCurrentSyncAnswer(displayTemplate)
  const pendingCheck = hasPendingTemplateChanges(displayTemplate)
  const checkInProgress = checking || isPlatformCheckRunning(displayTemplate, 'apple') || isPlatformCheckRunning(displayTemplate, 'google')
  const canRequestCheck = platformNeedsCheck(displayTemplate, 'apple') || platformNeedsCheck(displayTemplate, 'google')
  const isPrepay = client?.billing_mode === 'prepay'
  const showProgress = shouldShowProgress(appleProgress) || shouldShowProgress(googleProgress)

  const etaText = useMemo(() => getEtaText(displayTemplate), [displayTemplate])
  const syncCostCredits = useMemo(() => {
    if (!currentAnswerReady) return null
    return getSyncCostCredits({
      creditMatrix,
      passTypeId: displayTemplate?.pass_type_id,
      numPasses: syncCounts.live,
    })
  }, [creditMatrix, displayTemplate?.pass_type_id, currentAnswerReady, syncCounts.live])
  const currentSyncAnswerKey = useMemo(() => JSON.stringify({
    template_id: displayTemplate?.template_id,
    apple_checked_revision: displayTemplate?.apple_checked_revision ?? 0,
    google_checked_revision: displayTemplate?.google_checked_revision ?? 0,
    apple_sync_count: syncCounts.apple,
    google_sync_count: syncCounts.google,
    unique_sync_count: syncCounts.unique,
  }), [displayTemplate, syncCounts.apple, syncCounts.google, syncCounts.unique])

  const availableCredits = Number(creditBalance ?? 0)
  const hasInsufficientCredits = Boolean(
    isPrepay
    && currentAnswerReady
    && syncCounts.unique > 0
    && syncCostCredits != null
    && availableCredits < Number(syncCostCredits)
  )
  const syncExecutionInProgress = Boolean(syncStartCounts && syncCounts.unique > 0)
  const syncAlreadyQueuedForCurrentAnswer = currentAnswerReady && lastQueuedSyncKey === currentSyncAnswerKey
  const canSynchronise = currentAnswerReady
    && syncCounts.unique > 0
    && !hasInsufficientCredits
    && !syncExecutionInProgress
    && !syncAlreadyQueuedForCurrentAnswer
  const showInsufficientCreditsMessage = hasInsufficientCredits

  useEffect(() => {
    if (!open || creditMatrix.length) return
    loadCreditMatrix()
  }, [open, creditMatrix.length])

  useEffect(() => {
    if (!currentTemplate) return

    const isSyncRunning = Boolean(currentTemplate.sync_execution_in_progress)
    const isSyncComplete = !isSyncRunning && Number(syncCounts.unique ?? 0) === 0

    if (!open && notifyOnBackgroundSyncComplete && isSyncComplete) {
      setSyncCompletedPopupOpen(true)
      setLastQueuedSyncKey(null)
      setNotifyOnBackgroundSyncComplete(false)
    }
  }, [currentTemplate, open, syncCounts.unique, notifyOnBackgroundSyncComplete])

  const loadCreditMatrix = async () => {
    const result = await getCreditMatrix({ apiSource, apiKey })
    if (result.ok) setCreditMatrix(result.matrix ?? [])
  }

  const doRequestCheck = async () => {
    setChecking(true)
    try {
      enqueueSnackbar({ message: 'Starting sync check', variant: 'light' })
      const result = await requestSyncCheck({
        apiSource,
        apiKey,
        templateId: displayTemplate.template_id,
        apple: platformNeedsCheck(displayTemplate, 'apple'),
        google: platformNeedsCheck(displayTemplate, 'google'),
      })

      if (result.error) {
        showActionError(result.error, "Error checking synchronisation status")
        setChecking(false)
      } else {
        if (result.template_sync_state) {
          setTemplates(currentTemplates => currentTemplates.map(item => (
            item.template_id === currentTemplate.template_id
              ? { ...item, ...result.template_sync_state }
              : item
          )))
          setOptimisticBaseline(extractSyncSnapshot(currentTemplate))
          setOptimisticSyncState(result.template_sync_state)
        } else {
          setChecking(false)
        }
        enqueueSnackbar({ message: 'Sync check queued', variant: 'success' })
      }
    } catch (err) {
      setChecking(false)
      throw err
    }
  }

  const doSyncPasses = async () => {
    setSyncing(true)
    setSyncStartCounts({
      apple: Number(syncCounts.apple ?? 0),
      google: Number(syncCounts.google ?? 0),
      unique: Number(syncCounts.unique ?? 0),
    })
    enqueueSnackbar({ message: 'Synchronising passes', variant: 'light' })

    const result = await syncPasses({ apiSource, apiKey, templateId: currentTemplate.template_id })

    if (result.error) {
      showActionError(result.error, "Error synchronising passes")
      setSyncing(false)
      setSyncStartCounts(null)
    } else {
      const queuedApple = Number(result?.status?.apple ?? 0)
      const queuedGoogle = Number(result?.status?.google ?? 0)
      const queuedTotal = Number(result?.status?.total ?? (queuedApple + queuedGoogle))
      const hasQueuedSyncWork = queuedTotal > 0

      setLastQueuedSyncKey(currentSyncAnswerKey)
      setTemplates(currentTemplates => currentTemplates.map(item => (
        item.template_id === currentTemplate.template_id
          ? { ...item, sync_execution_in_progress: hasQueuedSyncWork }
          : item
      )))

      if (!hasQueuedSyncWork) setSyncStartCounts(null)
      setSyncing(false)
      enqueueSnackbar({ message: 'Synchronisation queued', variant: 'success' })
    }
  }

  const close = () => {
    if (Boolean(currentTemplate?.sync_execution_in_progress)) {
      setNotifyOnBackgroundSyncComplete(true)
    }
    setOpen(false)
    onCloseComplete?.()
  }

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') return
    close()
  }

  if (!displayTemplate) return null

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="sync-dialog-title"
        aria-describedby="sync-dialog-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="sync-dialog-title">
          Synchronise Passes : {currentTemplate.name}
        </DialogTitle>

        <DialogContent>
          <p className='mb-5'>
            Synchronisation pushes template changes to already issued passes. Before you can synchronise,
            Studio must calculate which issued passes are actually affected.
          </p>

          {showProgress &&
            <div className='mb-5'>
              <SyncProgressBlock
                platformLabel="Apple"
                progress={appleProgress}
                scopeLabel="installed Apple passes"
                scopeCount={Number(appleProgress.total ?? 0)}
              />
              <SyncProgressBlock
                platformLabel="Google"
                progress={googleProgress}
                scopeLabel="issued passes"
                scopeCount={Number(googleProgress.total ?? 0)}
              />
              {etaText && <p className='mt-5 mb-0'>Estimated time remaining: {etaText}</p>}
            </div>
          }

          {pendingCheck && !checkInProgress &&
            <p className='mb-5'>
              Template changes have been made since the last completed sync check.
              Start a new check to calculate how many passes currently require synchronisation.
            </p>
          }

          {currentAnswerReady &&
            <div className='mb-5'>
              <p className='mb-2'>
                Apple affected passes : {syncCounts.apple}<br />
                Google affected passes : {syncCounts.google}<br />
                Live affected passes : {syncCounts.live}<br />
                Test affected passes : {syncCounts.test}<br />
                Unique affected passes : {syncCounts.unique}
              </p>
              {isPrepay && syncCostCredits != null &&
                <p className='mb-0'>
                  Sync cost : {Number(syncCostCredits).toLocaleString()} credits
                </p>
              }
            </div>
          }

          {isPrepay && currentAnswerReady && syncCounts.unique > 0 && showInsufficientCreditsMessage &&
            <Alert severity="warning" className='mb-5'>
              Insufficient credits to run this synchronisation. Please top up your credit balance and try again.
            </Alert>
          }

          {syncStartCounts &&
            <div className='mb-5'>
              <strong>Synchronisation progress</strong>
              <SyncExecutionProgress
                platformLabel="Apple"
                startCount={Number(syncStartCounts.apple ?? 0)}
                remainingCount={Number(syncCounts.apple ?? 0)}
              />
              <SyncExecutionProgress
                platformLabel="Google"
                startCount={Number(syncStartCounts.google ?? 0)}
                remainingCount={Number(syncCounts.google ?? 0)}
              />
              {Number(syncCounts.unique ?? 0) === 0 &&
                <Alert severity="success" className='mt-3'>
                  Synchronisation complete.
                </Alert>
              }
            </div>
          }

          {!pendingCheck && !checkInProgress && currentAnswerReady && syncCounts.unique === 0 &&
            <p className='mb-5'>Nothing to synchronise. All passes are up-to-date.</p>
          }
        </DialogContent>

        <DialogActions>
          <div className="flex flex-row justify-between w-full p-5 gap-5">
            <Button variant="outlined" onClick={close} disabled={processing || checking}>Close</Button>
            <div className="flex flex-row gap-3">
              <Button
                variant="outlined"
                onClick={doRequestCheck}
                disabled={processing || checking || syncing || checkInProgress || syncExecutionInProgress || !canRequestCheck}
              >
                <SearchOutlinedIcon className="mr-5" />
                Check Affected Passes
              </Button>
              <Button
                variant="contained"
                onClick={doSyncPasses}
                autoFocus
                disabled={processing || checking || !canSynchronise}
              >
                <SyncOutlinedIcon className="mr-5" />
                Synchronise Passes
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>

      <ActionErrorDialog title="Synchronisation failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />

      <Dialog
        open={syncCompletedPopupOpen}
        onClose={() => setSyncCompletedPopupOpen(false)}
        aria-labelledby="sync-complete-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="sync-complete-title">Synchronisation complete</DialogTitle>
        <DialogContent>
          <p className='mb-0'>
            Pass synchronisation has completed for {currentTemplate.name}.
          </p>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSyncCompletedPopupOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const extractSyncSnapshot = template => ({
  apple_sync_revision: template?.apple_sync_revision ?? 0,
  google_sync_revision: template?.google_sync_revision ?? 0,
  apple_checked_revision: template?.apple_checked_revision ?? 0,
  google_checked_revision: template?.google_checked_revision ?? 0,
  apple_check_status: template?.apple_check_status ?? 'stale',
  google_check_status: template?.google_check_status ?? 'stale',
  apple_candidate_total: template?.apple_candidate_total ?? 0,
  google_candidate_total: template?.google_candidate_total ?? 0,
  apple_checked_so_far: template?.apple_checked_so_far ?? 0,
  google_checked_so_far: template?.google_checked_so_far ?? 0,
  apple_sync_count: template?.apple_sync_count ?? 0,
  google_sync_count: template?.google_sync_count ?? 0,
  unique_sync_count: template?.unique_sync_count ?? 0,
  apple_check_requested_ts: template?.apple_check_requested_ts ?? null,
  google_check_requested_ts: template?.google_check_requested_ts ?? null,
  apple_check_started_ts: template?.apple_check_started_ts ?? null,
  google_check_started_ts: template?.google_check_started_ts ?? null,
  apple_check_completed_ts: template?.apple_check_completed_ts ?? null,
  google_check_completed_ts: template?.google_check_completed_ts ?? null,
})

const SyncProgressBlock = ({ platformLabel, progress, scopeLabel, scopeCount = 0 }) => {
  const showBlock = shouldShowProgress(progress)
  if (!showBlock) return null

  const isDeterminate = progress.status === 'running'
    ? progress.total > 0
    : progress.current || progress.status === 'queued'

  const progressValue = progress.status === 'running'
    ? progress.percent
    : progress.current
      ? 100
      : progress.status === 'queued'
        ? 0
        : 0

  return (
    <div className='mb-5 last:mb-0'>
      <div className='flex flex-row justify-between items-center mb-2'>
        <div>
          <strong>{platformLabel}</strong>
          <div className='text-xs text-gray-500'>
            {scopeCount.toLocaleString()} {scopeLabel}
          </div>
        </div>
        <span className='text-sm capitalize'>{progress.status}</span>
      </div>
      <LinearProgress variant={isDeterminate ? "determinate" : "indeterminate"} value={progressValue} />
      <div className='mt-2 text-sm'>
        {progress.current
          ? progress.syncCount > 0
            ? `${progress.syncCount.toLocaleString()} passes currently need sync`
            : 'No sync needed'
          : progress.status === 'queued'
            ? `0 / ${progress.total.toLocaleString()} checked, ${progress.total.toLocaleString()} left to check`
            : progress.total > 0
          ? `${progress.checked.toLocaleString()} / ${progress.total.toLocaleString()} checked, ${(progress.total - progress.checked).toLocaleString()} left to check, ${progress.syncCount.toLocaleString()} affected so far`
          : progress.running
            ? 'Preparing candidate list'
            : progress.status === 'error'
              ? 'Check failed'
              : 'Awaiting check'}
      </div>
    </div>
  )
}

const SyncExecutionProgress = ({ platformLabel, startCount = 0, remainingCount = 0 }) => {
  if (startCount <= 0) return null

  const completed = Math.max(startCount - remainingCount, 0)
  const percent = Math.min(Math.round((completed / startCount) * 100), 100)

  return (
    <div className='mt-3'>
      <div className='flex flex-row justify-between items-center mb-1'>
        <span>{platformLabel}</span>
        <span className='text-sm'>{completed.toLocaleString()} / {startCount.toLocaleString()} synced</span>
      </div>
      <LinearProgress variant="determinate" value={percent} />
    </div>
  )
}

const shouldShowProgress = progress => (
  progress.running
  || progress.current
  || progress.status === 'queued'
  || progress.status === 'error'
  || progress.syncCount > 0
  || progress.total > 0
)

const getEtaText = template => {
  const backendEtaText = template?.sync_progress?.eta_text
  if (backendEtaText) return backendEtaText

  const etaApple = getPlatformEtaSeconds(template, 'apple')
  const etaGoogle = getPlatformEtaSeconds(template, 'google')
  const seconds = Math.max(etaApple ?? 0, etaGoogle ?? 0)

  if (!seconds) return null
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`
  return `${Math.ceil(seconds / 3600)}h`
}

const getPlatformEtaSeconds = (template, platform) => {
  const startedTs = template?.[`${platform}_check_started_ts`]
  const total = Number(template?.[`${platform}_candidate_total`] ?? 0)
  const checked = Number(template?.[`${platform}_checked_so_far`] ?? 0)
  const status = String(template?.[`${platform}_check_status`] ?? 'stale')

  if (status !== 'running' || !startedTs || checked <= 0 || checked >= total || total <= 0) return null

  const started = moment(startedTs, "YYYYMMDDHHmmss")
  if (!started.isValid()) return null

  const elapsedSeconds = Math.max(moment().diff(started, 'seconds'), 1)
  const secondsPerItem = elapsedSeconds / checked

  return Math.ceil((total - checked) * secondsPerItem)
}

const getSyncCostCredits = ({ creditMatrix = [], passTypeId, numPasses }) => {
  const matrixKey = passTypeId?.startsWith("smartix_")
    ? `managed ${passTypeId.replace("smartix_", "")} passes`
    : 'all standard passes'

  const matrixRow = creditMatrix.find(item => item.pass_type === matrixKey)
  const syncCost = Number(matrixRow?.sync ?? 0)

  return syncCost * Number(numPasses ?? 0)
}

export default SynchroniseDialog
